// src/components/Dashboard/widgets/ActiveJobsWidget.tsx
// Widget 1 — Active Jobs Summary
// Click any job card → Jobs module at that record.
// Click on-hold badge → Jobs filtered to ON_HOLD.
import { Briefcase, ChevronRight, CheckCircle } from 'lucide-react'
import { MOCK_JOBS, PROJECT_TYPE_META } from '../../../utils/mockData'
import { MOCK_ALLOCATIONS, getJobWeekCrew, WEEK_DAYS } from '../../../utils/mockDataRoster'
import { formatCurrency } from '../../../utils/formatters'
import { WidgetProps } from '../types'
import { useDeepLinkNavigation } from '../../../hooks/useDeepLinkNavigation'

export function ActiveJobsWidget({ onNavigate, scopedJobIds }: WidgetProps) {
  const { navigate } = useDeepLinkNavigation('dashboard', onNavigate)

  const all       = MOCK_JOBS
  const scoped    = scopedJobIds ? all.filter(j => scopedJobIds.includes(j.id)) : all
  const active    = scoped.filter(j => j.status === 'ACTIVE')
  const onHold    = scoped.filter(j => j.status === 'ON_HOLD')
  const complete  = scoped.filter(j => ['COMPLETED', 'CLOSED'].includes(j.status))
  const top       = active.slice(0, 4)

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
      {/* Header row */}
      <div
        className="px-5 py-3.5 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => navigate('jobs')}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Briefcase className="w-4 h-4 text-blue-600" />
          <span className="font-semibold text-gray-900 text-sm">Active jobs</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
            {active.length}
          </span>
          {onHold.length > 0 && (
            <span
              className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium cursor-pointer"
              onClick={e => { e.stopPropagation(); navigate('jobs', { openTab: 'on-hold' }) }}
            >
              {onHold.length} on hold
            </span>
          )}
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 divide-x border-b bg-gray-50/60">
        {[
          { label: 'Active',   value: active.length,   cls: 'text-blue-600'  },
          { label: 'On hold',  value: onHold.length,   cls: 'text-amber-600' },
          { label: 'Complete', value: complete.length,  cls: 'text-green-600' },
        ].map(s => (
          <div key={s.label} className="px-4 py-2.5 text-center">
            <div className={`text-xl font-bold ${s.cls}`}>{s.value}</div>
            <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Job rows */}
      <div className="divide-y divide-gray-50 flex-1">
        {top.map(job => {
          const meta  = (PROJECT_TYPE_META as any)[job.type] ?? (PROJECT_TYPE_META as any).SIGNALS
          const crew  = getJobWeekCrew(job.id, WEEK_DAYS, MOCK_ALLOCATIONS).length
          const pct   = job.progressPercent ?? 0

          return (
            <div
              key={job.id}
              className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors group"
              onClick={() => navigate('jobs', { openId: job.id })}
            >
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">{job.jobNumber}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${meta.bg} ${meta.color}`}>
                    {meta.label}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate mt-0.5">{job.clientName}</p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-gray-400 tabular-nums w-7 text-right">{pct}%</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs font-medium text-gray-700">{crew} crew</div>
                <div className="text-xs text-gray-400">{formatCurrency(job.contractValue)}</div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-gray-200 group-hover:text-gray-400 flex-shrink-0 transition-colors" />
            </div>
          )
        })}

        {active.length === 0 && (
          <div className="px-5 py-8 text-center">
            <CheckCircle className="w-6 h-6 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No active jobs</p>
          </div>
        )}
      </div>

      {/* Footer */}
      {active.length > 4 && (
        <button
          onClick={() => navigate('jobs')}
          className="w-full px-5 py-2.5 border-t text-xs text-blue-600 hover:bg-blue-50 transition-colors text-center font-medium"
        >
          View all {active.length} active jobs →
        </button>
      )}
    </div>
  )
}
